import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isHomePage = location.pathname === '/';
  
  const shouldUseWhiteText = isHomePage ? !scrolled : true;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex justify-between items-center">
          <Link 
            to="/" 
            className="font-display text-2xl font-bold text-barbecue-600 transition-colors duration-300"
          >
            Žar & Bar
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6">
            <Link 
              to="/" 
              className={`nav-link ${scrolled ? 'text-foreground' : 'text-white'} ${isActive('/') ? 'nav-link-active' : ''}`}
            >
              Domača stran
            </Link>
            <Link 
              to="/menu" 
              className={`nav-link ${scrolled ? 'text-foreground' : 'text-white'} ${isActive('/menu') ? 'nav-link-active' : ''}`}
            >
              Meni
            </Link>
            <Link 
              to="/about" 
              className={`nav-link ${scrolled ? 'text-foreground' : 'text-white'} ${isActive('/about') ? 'nav-link-active' : ''}`}
            >
              O nas
            </Link>
            <Link 
              to="/gallery" 
              className={`nav-link ${scrolled ? 'text-foreground' : 'text-white'} ${isActive('/gallery') ? 'nav-link-active' : ''}`}
            >
              Galerija
            </Link>
            <Link 
              to="/events" 
              className={`nav-link ${scrolled ? 'text-foreground' : 'text-white'} ${isActive('/events') ? 'nav-link-active' : ''}`}
            >
              Dogodki
            </Link>
            <Link 
              to="/contact" 
              className={`nav-link ${scrolled ? 'text-foreground' : 'text-white'} ${isActive('/contact') ? 'nav-link-active' : ''}`}
            >
              Kontakt
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden focus:outline-none p-2 rounded-full ${shouldUseWhiteText && !scrolled ? 'text-white bg-white/20 hover:bg-white/30' : 'text-foreground bg-gray-100 hover:bg-gray-200'}`}
            aria-label={isOpen ? 'Zapri meni' : 'Odpri meni'}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation - Fixed height issue */}
      <div 
        className={`fixed left-0 right-0 bg-white z-40 transition-transform duration-300 ease-in-out transform ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } md:hidden`}
        style={{ top: '60px' }}
      >
        <nav className="flex flex-col p-6 space-y-6">
          <Link 
            to="/" 
            className={`text-lg font-medium px-4 py-3 rounded-full ${isActive('/') ? 'bg-barbecue-100 text-barbecue-600' : 'text-foreground hover:bg-gray-100'}`}
            onClick={() => setIsOpen(false)}
          >
            Domača stran
          </Link>
          <Link 
            to="/menu" 
            className={`text-lg font-medium px-4 py-3 rounded-full ${isActive('/menu') ? 'bg-barbecue-100 text-barbecue-600' : 'text-foreground hover:bg-gray-100'}`}
            onClick={() => setIsOpen(false)}
          >
            Meni
          </Link>
          <Link 
            to="/about" 
            className={`text-lg font-medium px-4 py-3 rounded-full ${isActive('/about') ? 'bg-barbecue-100 text-barbecue-600' : 'text-foreground hover:bg-gray-100'}`}
            onClick={() => setIsOpen(false)}
          >
            O nas
          </Link>
          <Link 
            to="/gallery" 
            className={`text-lg font-medium px-4 py-3 rounded-full ${isActive('/gallery') ? 'bg-barbecue-100 text-barbecue-600' : 'text-foreground hover:bg-gray-100'}`}
            onClick={() => setIsOpen(false)}
          >
            Galerija
          </Link>
          <Link 
            to="/events" 
            className={`text-lg font-medium px-4 py-3 rounded-full ${isActive('/events') ? 'bg-barbecue-100 text-barbecue-600' : 'text-foreground hover:bg-gray-100'}`}
            onClick={() => setIsOpen(false)}
          >
            Dogodki
          </Link>
          <Link 
            to="/contact" 
            className={`text-lg font-medium px-4 py-3 rounded-full ${isActive('/contact') ? 'bg-barbecue-100 text-barbecue-600' : 'text-foreground hover:bg-gray-100'}`}
            onClick={() => setIsOpen(false)}
          >
            Kontakt
          </Link>
          
          {/* CTA button */}
          <Link 
            to="/contact" 
            className="mt-4 btn-primary flex items-center justify-center rounded-full"
            onClick={() => setIsOpen(false)}
          >
            Rezerviraj mizo
            <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;